import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Archive, BadgeCheck, CreditCard, Pencil, Plus, ShieldCheck } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

const EMPTY_FORM = {
  id: null,
  payer_id: "",
  member_id: "",
  plan_name: "",
  effective_date: "",
  expiry_date: "",
  is_primary: true,
};

export default function PatientHmoTab({ patientId }) {
  const [profiles, setProfiles] = useState([]);
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const activeProfiles = useMemo(() => profiles.filter((profile) => !profile.archived), [profiles]);
  const visibleProfiles = showArchived ? profiles : activeProfiles;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      const [profilesRes, payersRes] = await Promise.all([
        supabase.rpc("list_patient_hmo_profiles", { p_patient_id: patientId }),
        supabase.rpc("list_insurance_payers", { p_include_archived: false }),
      ]);
      if (cancelled) return;
      if (profilesRes.error) setError(profilesRes.error.message || "Could not load HMO cards.");
      setProfiles(profilesRes.data ?? []);
      setPayers(payersRes.data ?? []);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [patientId]);

  function openNewForm() {
    setForm({
      ...EMPTY_FORM,
      is_primary: activeProfiles.length === 0,
    });
    setFormOpen(true);
    setError("");
  }

  function openEditForm(profile) {
    if (profile.archived) return;
    setForm({
      id: profile.id,
      payer_id: profile.payer_id || "",
      member_id: profile.member_id || "",
      plan_name: profile.plan_name || "",
      effective_date: profile.effective_date || "",
      expiry_date: profile.expiry_date || "",
      is_primary: !!profile.is_primary,
    });
    setFormOpen(true);
    setError("");
  }

  function setFormValue(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function reloadProfiles() {
    const { data, error: loadError } = await supabase.rpc("list_patient_hmo_profiles", { p_patient_id: patientId });
    if (loadError) {
      setError(loadError.message || "Could not refresh HMO cards.");
      return;
    }
    setProfiles(data ?? []);
  }

  async function saveProfile(event) {
    event?.preventDefault?.();
    const formElement = event?.currentTarget?.form || event?.currentTarget;
    const draft = formElement?.elements ? {
      ...form,
      payer_id: formElement.elements.payer_id?.value ?? form.payer_id,
      member_id: formElement.elements.member_id?.value ?? form.member_id,
      plan_name: formElement.elements.plan_name?.value ?? form.plan_name,
      effective_date: formElement.elements.effective_date?.value ?? form.effective_date,
      expiry_date: formElement.elements.expiry_date?.value ?? form.expiry_date,
      is_primary: !!formElement.elements.is_primary?.checked,
    } : form;
    if (!draft.payer_id || !draft.member_id.trim()) {
      setError("Payer and member ID are required.");
      return;
    }
    setSaving(true);
    setError("");
    const { error: saveError } = await supabase.rpc("upsert_patient_hmo_profile", {
      p_input: {
        ...(form.id ? { id: form.id } : {}),
        patient_id: patientId,
        payer_id: draft.payer_id,
        member_id: draft.member_id.trim(),
        plan_name: draft.plan_name.trim() || null,
        effective_date: draft.effective_date || null,
        expiry_date: draft.expiry_date || null,
        is_primary: !!draft.is_primary,
      },
    });
    setSaving(false);
    if (saveError) {
      setError(saveError.message || "Could not save HMO card.");
      return;
    }
    setFormOpen(false);
    await reloadProfiles();
  }

  async function archiveProfile(profile) {
    if (profile.archived) return;
    setSaving(true);
    setError("");
    const { error: archiveError } = await supabase.rpc("archive_patient_hmo_profile", { p_profile_id: profile.id });
    setSaving(false);
    if (archiveError) {
      setError(archiveError.message || "Could not archive HMO card.");
      return;
    }
    await reloadProfiles();
  }

  async function setPrimary(profile) {
    if (profile.archived || profile.is_primary) return;
    setSaving(true);
    setError("");
    const { error: saveError } = await supabase.rpc("upsert_patient_hmo_profile", {
      p_input: {
        id: profile.id,
        patient_id: patientId,
        payer_id: profile.payer_id,
        member_id: profile.member_id,
        plan_name: profile.plan_name || null,
        effective_date: profile.effective_date || null,
        expiry_date: profile.expiry_date || null,
        is_primary: true,
      },
    });
    setSaving(false);
    if (saveError) {
      setError(saveError.message || "Could not set primary HMO card.");
      return;
    }
    await reloadProfiles();
  }

  return (
    <section data-testid="patient-hmo-tab" className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-950">HMO cards</h2>
            <p className="mt-1 text-sm text-slate-500">Saved payer and member details for checkout prefill.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(event) => setShowArchived(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Show archived
            </label>
            <button
              type="button"
              onClick={openNewForm}
              data-testid="hmo-add-card"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-950 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" strokeWidth={2.25} />
              Add card
            </button>
          </div>
        </div>
        {error ? (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}
      </div>

      {formOpen ? (
        <form onSubmit={saveProfile} className="rounded-lg border border-slate-200 bg-white p-4" data-testid="hmo-card-form">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-semibold text-slate-700">
              Payer
              <select
                name="payer_id"
                value={form.payer_id}
                onChange={(event) => setFormValue("payer_id", event.target.value)}
                data-testid="hmo-form-payer"
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500"
              >
                <option value="">Select payer</option>
                {payers.map((payer) => (
                  <option key={payer.id} value={payer.id}>
                    {payer.name}{payer.short_name ? ` (${payer.short_name})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-semibold text-slate-700">
              Member ID
              <input
                name="member_id"
                value={form.member_id}
                onChange={(event) => setFormValue("member_id", event.target.value)}
                data-testid="hmo-form-member"
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500"
              />
            </label>
            <label className="text-sm font-semibold text-slate-700">
              Plan name
              <input
                name="plan_name"
                value={form.plan_name}
                onChange={(event) => setFormValue("plan_name", event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm font-semibold text-slate-700">
                Effective
                <input
                  name="effective_date"
                  type="date"
                  value={form.effective_date}
                  onChange={(event) => setFormValue("effective_date", event.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Expiry
                <input
                  name="expiry_date"
                  type="date"
                  value={form.expiry_date}
                  onChange={(event) => setFormValue("expiry_date", event.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500"
                />
              </label>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                name="is_primary"
                type="checkbox"
                checked={form.is_primary}
                onChange={(event) => setFormValue("is_primary", event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Set as primary
            </label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setFormOpen(false)} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={saveProfile} disabled={saving} data-testid="hmo-form-save" className="rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60">
                {saving ? "Saving..." : "Save card"}
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">Loading HMO cards...</div>
      ) : visibleProfiles.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <CreditCard className="mx-auto h-8 w-8 text-slate-300" strokeWidth={2.25} />
          <h3 className="mt-3 text-base font-bold text-slate-950">No saved HMO cards</h3>
          <p className="mt-1 text-sm text-slate-500">Add the patient's card once so checkout can prefill payer and member ID.</p>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {visibleProfiles.map((profile) => (
            <HmoCard
              key={profile.id}
              profile={profile}
              saving={saving}
              onEdit={() => openEditForm(profile)}
              onSetPrimary={() => setPrimary(profile)}
              onArchive={() => archiveProfile(profile)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function HmoCard({ profile, saving, onEdit, onSetPrimary, onArchive }) {
  const expired = isExpired(profile.expiry_date);
  return (
    <article className={`rounded-lg border bg-white p-4 ${profile.archived ? "border-slate-200 opacity-70" : expired ? "border-amber-300" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-bold text-slate-950">{profile.payer_name || "HMO payer"}</h3>
            {profile.payer_short_name ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">{profile.payer_short_name}</span> : null}
            {profile.is_primary && !profile.archived ? (
              <span className="inline-flex items-center gap-1 rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-teal-700">
                <BadgeCheck className="h-3 w-3" strokeWidth={2.25} /> Primary
              </span>
            ) : null}
            {expired ? (
              <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                <AlertTriangle className="h-3 w-3" strokeWidth={2.25} /> Expired
              </span>
            ) : null}
            {profile.archived ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">Archived</span> : null}
          </div>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <Fact label="Member ID" value={profile.member_id} />
            <Fact label="Plan" value={profile.plan_name || "-"} />
            <Fact label="Effective" value={formatDate(profile.effective_date)} />
            <Fact label="Expiry" value={formatDate(profile.expiry_date)} />
          </dl>
        </div>
        <ShieldCheck className="h-5 w-5 shrink-0 text-slate-300" strokeWidth={2.25} />
      </div>
      {!profile.archived ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <ActionButton onClick={onEdit} disabled={saving} icon={Pencil}>Edit</ActionButton>
          {!profile.is_primary ? <ActionButton onClick={onSetPrimary} disabled={saving} icon={BadgeCheck}>Set as Primary</ActionButton> : null}
          <ActionButton onClick={onArchive} disabled={saving} icon={Archive} testId={`hmo-card-archive-${profile.id}`}>Archive</ActionButton>
        </div>
      ) : null}
    </article>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function ActionButton({ children, icon: Icon, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
      {children}
    </button>
  );
}

function isExpired(value) {
  if (!value) return false;
  return value < new Date().toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return "-";
  return value;
}
